import React, { useCallback, useEffect, useState } from 'react';
import { TypeIcon, Heading1Icon, Heading2Icon, Heading3Icon, ListIcon, ListOrderedIcon, QuoteIcon, MinusIcon } from 'lucide-react';
import type { BlockType } from '../types';
interface CommandOption {
  type: BlockType;
  label: string;
  description: string;
  icon: React.ReactNode;
}
const COMMANDS: CommandOption[] = [{
  type: 'text',
  label: 'Text',
  description: 'Plain text block',
  icon: <TypeIcon className="w-4 h-4" data-id="element-160" />
}, {
  type: 'heading1',
  label: 'Heading 1',
  description: 'Large section heading',
  icon: <Heading1Icon className="w-4 h-4" data-id="element-161" />
}, {
  type: 'heading2',
  label: 'Heading 2',
  description: 'Medium section heading',
  icon: <Heading2Icon className="w-4 h-4" data-id="element-162" />
}, {
  type: 'heading3',
  label: 'Heading 3',
  description: 'Small section heading',
  icon: <Heading3Icon className="w-4 h-4" data-id="element-163" />
}, {
  type: 'bulletList',
  label: 'Bulleted list',
  description: 'Unordered list item',
  icon: <ListIcon className="w-4 h-4" data-id="element-164" />
}, {
  type: 'numberedList',
  label: 'Numbered list',
  description: 'Ordered list item',
  icon: <ListOrderedIcon className="w-4 h-4" data-id="element-165" />
}, {
  type: 'quote',
  label: 'Quote',
  description: 'Blockquote text',
  icon: <QuoteIcon className="w-4 h-4" data-id="element-166" />
}, {
  type: 'divider',
  label: 'Divider',
  description: 'Horizontal rule',
  icon: <MinusIcon className="w-4 h-4" data-id="element-167" />
}];
interface CommandMenuProps {
  filter: string;
  onSelect: (type: BlockType) => void;
  onClose: () => void;
}
export function CommandMenu({
  filter,
  onSelect,
  onClose
}: CommandMenuProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const filtered = COMMANDS.filter(cmd => cmd.label.toLowerCase().includes(filter.toLowerCase()) || cmd.type.toLowerCase().includes(filter.toLowerCase()));
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        onSelect(filtered[selectedIndex].type);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [filtered, selectedIndex, onSelect, onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);
  if (filtered.length === 0) {
    return <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white rounded-lg border border-stone-200 shadow-lg py-2 px-1" data-id="element-168">
        <p className="text-sm text-stone-400 px-3 py-2" data-id="element-169">No results</p>
      </div>;
  }
  return <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-white rounded-lg border border-stone-200 shadow-lg py-1 overflow-hidden" role="listbox" aria-label="Block type commands" data-id="element-170">
      <div className="px-3 py-1.5" data-id="element-171">
        <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wider" data-id="element-172">
          Blocks
        </p>
      </div>
      {filtered.map((cmd, i) => <button key={cmd.type} role="option" aria-selected={i === selectedIndex} className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors duration-100 rounded-md mx-0 ${i === selectedIndex ? 'bg-stone-100' : ''}`} onMouseEnter={() => setSelectedIndex(i)} onMouseDown={e => {
      e.preventDefault();
      onSelect(cmd.type);
    }} data-id="element-173">
          <span className="flex items-center justify-center w-8 h-8 rounded-md border border-stone-200 bg-white text-stone-500" data-id="element-174">
            {cmd.icon}
          </span>
          <span className="flex flex-col min-w-0" data-id="element-175">
            <span className="text-sm font-medium text-stone-800" data-id="element-176">
              {cmd.label}
            </span>
            <span className="text-xs text-stone-400" data-id="element-177">{cmd.description}</span>
          </span>
        </button>)}
    </div>;
}